import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import { updatesService } from '../../services/apiService';
import api from '../../services/api';
import PostCard from '../../components/ui/post-card';

const ROLE_OPTIONS = [
  'ALL',
  'STUDENT',
  'FACULTY',
  'HOD',
  'ADMIN',
  'SUPER_ADMIN',
  'ACCOUNTS_SECTION',
  'LIBRARY_STAFF',
  'EXAM_SECTION',
  'CEO',
  'PRINCIPAL',
  'VICE_PRINCIPAL',
  'DEAN',
];

const SEMESTER_OPTIONS = ['SEM_1', 'SEM_2', 'SEM_3', 'SEM_4', 'SEM_5', 'SEM_6', 'SEM_7', 'SEM_8'];
const DEPARTMENT_OPTIONS = ['CS', 'CD', 'CI', 'CY', 'EC', 'ME'];
const SECTION_OPTIONS = ['S', 'D', 'I', 'Y', 'C', 'E'];

const formatDate = (value) => {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  return new Intl.DateTimeFormat('en-IN', { dateStyle: 'medium', timeStyle: 'short' }).format(parsed);
};

const MAX_UPLOAD_BYTES = 100 * 1024;

const fileToDataUrl = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = () => reject(new Error('Unable to read image file'));
  reader.readAsDataURL(file);
});

const dataUrlToBlob = async (dataUrl) => {
  const response = await fetch(dataUrl);
  return response.blob();
};

const canvasToDataUrl = (canvas, quality) => canvas.toDataURL('image/jpeg', quality);

const compressImageToLimit = async (file, maxBytes = MAX_UPLOAD_BYTES) => {
  const sourceDataUrl = await fileToDataUrl(file);

  const image = await new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Unable to load selected image'));
    img.src = sourceDataUrl;
  });

  let width = image.width;
  let height = image.height;

  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (!context) {
    throw new Error('Unable to initialize image compressor');
  }

  const targetMimeType = 'image/jpeg';
  let bestBlob = null;

  for (let scaleStep = 0; scaleStep < 12; scaleStep += 1) {
    canvas.width = Math.max(120, Math.round(width));
    canvas.height = Math.max(120, Math.round(height));
    context.clearRect(0, 0, canvas.width, canvas.height);
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(image, 0, 0, canvas.width, canvas.height);

    const qualities = [0.82, 0.72, 0.62, 0.52, 0.42, 0.32, 0.24, 0.16, 0.1, 0.06];

    for (const quality of qualities) {
      const compressedDataUrl = canvasToDataUrl(canvas, quality);
      const compressedBlob = await dataUrlToBlob(compressedDataUrl);

      if (!bestBlob || compressedBlob.size < bestBlob.size) {
        bestBlob = compressedBlob;
      }

      if (compressedBlob.size <= maxBytes) {
        return new File([compressedBlob], `notice-${Date.now()}.jpg`, { type: targetMimeType });
      }
    }

    width *= 0.78;
    height *= 0.78;
  }

  if (bestBlob) {
    return new File([bestBlob], `notice-${Date.now()}.jpg`, { type: 'image/jpeg' });
  }

  throw new Error('Unable to compress image');
};

const parseCsvToArray = (value) => String(value || '')
  .split(/[,\n|;]/)
  .map((entry) => entry.trim())
  .filter(Boolean);

const noticeToFormData = (notice) => ({
  title: notice?.title || '',
  content: notice?.content || '',
  targetRoles: Array.isArray(notice?.targetRoles) && notice.targetRoles.length ? notice.targetRoles : ['ALL'],
  studentSemesters: parseCsvToArray(notice?.audience?.semesters)[0] || '',
  studentSections: parseCsvToArray(notice?.audience?.sections)[0] || '',
  studentDepartments: parseCsvToArray(notice?.audience?.departments)[0] || '',
  isActive: notice?.isActive ?? true,
  existingImageUrl: notice?.imageUrl || null,
  existingImageStorage: notice?.imageStorage || null,
  existingImagePath: notice?.imagePath || null,
});

const quillModules = {
  toolbar: [
    [{ header: [1, 2, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link', 'blockquote'],
    ['clean'],
  ],
};

const quillFormats = [
  'header',
  'bold',
  'italic',
  'underline',
  'strike',
  'list',
  'bullet',
  'link',
  'blockquote',
];

const stripHtml = (value) => String(value || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

const escapeHtml = (value) => String(value || '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;');

const hasMarkdownSyntax = (value) => {
  const text = String(value || '');
  return /(\*\*[^*]+\*\*)|(__[^_]+__)|(\*[^*]+\*)|(_[^_]+_)|(`[^`]+`)|(\[[^\]]+\]\([^)]+\))|(^\s*[-*]\s+.+$)|(https?:\/\/\S+)/m.test(text);
};

const inlineMarkdownToHtml = (value) => {
  let html = escapeHtml(value);
  html = html.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/__([^_]+)__/g, '<strong>$1</strong>');
  html = html.replace(/\*([^*]+)\*/g, '<em>$1</em>');
  html = html.replace(/_([^_]+)_/g, '<em>$1</em>');
  html = html.replace(/`([^`]+)`/g, '<code>$1</code>');
  html = html.replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');
  html = html.replace(/(^|\s)(https?:\/\/[^\s<]+)/g, '$1<a href="$2" target="_blank" rel="noopener noreferrer">$2</a>');
  return html;
};

const markdownToHtml = (value) => {
  const lines = String(value || '').split(/\r?\n/);
  const output = [];
  let listBuffer = [];

  const flushList = () => {
    if (!listBuffer.length) return;
    output.push(`<ul>${listBuffer.join('')}</ul>`);
    listBuffer = [];
  };

  lines.forEach((rawLine) => {
    const line = rawLine.trim();

    if (!line) {
      flushList();
      return;
    }

    const bulletMatch = line.match(/^[-*]\s+(.+)$/);
    if (bulletMatch) {
      listBuffer.push(`<li>${inlineMarkdownToHtml(bulletMatch[1])}</li>`);
      return;
    }

    flushList();
    output.push(`<p>${inlineMarkdownToHtml(line)}</p>`);
  });

  flushList();
  return output.join('');
};

const NotificationsManagement = () => {
  const quillRef = useRef(null);
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [compressingImage, setCompressingImage] = useState(false);
  const [updates, setUpdates] = useState([]);
  const [students, setStudents] = useState([]);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [studentsError, setStudentsError] = useState('');
  const [studentPreviewOpen, setStudentPreviewOpen] = useState(false);
  const [selectedStudentUsns, setSelectedStudentUsns] = useState([]);
  const [studentSearchQuery, setStudentSearchQuery] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [editingNoticeId, setEditingNoticeId] = useState(null);
  const [removeExistingImage, setRemoveExistingImage] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    targetRoles: ['ALL'],
    studentSemesters: '',
    studentSections: '',
    studentDepartments: '',
    isActive: true,
    existingImageUrl: null,
    existingImageStorage: null,
    existingImagePath: null,
  });

  const isStudentTargetingEnabled = useMemo(
    () => formData.targetRoles.includes('ALL') || formData.targetRoles.includes('STUDENT'),
    [formData.targetRoles]
  );

  const canPost = useMemo(
    () => ['SUPER_ADMIN', 'ADMIN', 'HOD', 'EXAM_SECTION', 'CEO', 'PRINCIPAL', 'VICE_PRINCIPAL', 'DEAN'].includes(user?.role),
    [user?.role]
  );

  const canManageNotice = (notice) => {
    if (user?.role === 'SUPER_ADMIN') return true;
    return Boolean(user?.id && notice?.createdById && notice.createdById === user.id);
  };

  const loadStudents = useCallback(async () => {
    try {
      setStudentsLoading(true);
      setStudentsError('');
      const response = await api.get('/users/students');
      const loadedStudents = Array.isArray(response?.data?.data) ? response.data.data : [];
      setStudents(loadedStudents);
      return loadedStudents;
    } catch (error) {
      console.error('Failed to load students:', error);
      setStudents([]);
      setStudentsError(error?.response?.data?.error || 'Failed to load students for preview.');
      return [];
    } finally {
      setStudentsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (canPost && isStudentTargetingEnabled) {
      loadStudents();
    }
  }, [canPost, isStudentTargetingEnabled, loadStudents]);

  useEffect(() => {
    setSelectedStudentUsns([]);
  }, [formData.studentSemesters, formData.studentDepartments, formData.studentSections]);

  useEffect(() => {
    if (!studentPreviewOpen) {
      setStudentSearchQuery('');
    }
  }, [studentPreviewOpen]);

  const resetForm = () => {
    setEditingNoticeId(null);
    setRemoveExistingImage(false);
    setSelectedStudentUsns([]);
    setFormData({
      title: '',
      content: '',
      targetRoles: ['ALL'],
      studentSemesters: '',
      studentSections: '',
      studentDepartments: '',
      isActive: true,
      existingImageUrl: null,
      existingImageStorage: null,
      existingImagePath: null,
    });
    setImageFile(null);
  };

  const loadUpdates = useCallback(async () => {
    try {
      setLoading(true);
      const response = await updatesService.getManageUpdates();
      let allNotices = Array.isArray(response?.data) ? response.data : [];
      if (user?.id) {
        const pinnedStr = localStorage.getItem(`pinnedNotice_${user.id}`);
        if (pinnedStr) {
          try {
            const pinnedData = JSON.parse(pinnedStr);
            const noticeIndex = allNotices.findIndex(n => n.id === pinnedData.notificationId);
            if (noticeIndex > -1) {
              const [pinnedItem] = allNotices.splice(noticeIndex, 1);
              allNotices.unshift(pinnedItem);
            } else {
              localStorage.removeItem(`pinnedNotice_${user.id}`);
              window.dispatchEvent(new Event('local-pin-change'));
            }
          } catch(e) {
            console.error('Failed to parse pinned notice', e);
          }
        }
      }
      setUpdates(allNotices);
    } catch (error) {
      console.error('Failed to load updates:', error);
      toast.error('Unable to load current notifications.');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    loadUpdates();
  }, [loadUpdates]);

  useEffect(() => {
    const editor = quillRef.current?.getEditor?.();
    if (!editor) return;

    const handlePaste = (event) => {
      if (!canPost || submitting) return;

      const plainText = event.clipboardData?.getData('text/plain') || '';
      const htmlText = event.clipboardData?.getData('text/html') || '';

      if (!plainText || htmlText || !hasMarkdownSyntax(plainText)) return;

      event.preventDefault();

      const selection = editor.getSelection(true);
      const insertAt = selection?.index ?? editor.getLength();
      const converted = markdownToHtml(plainText);
      editor.clipboard.dangerouslyPasteHTML(insertAt, converted, 'user');
    };

    editor.root.addEventListener('paste', handlePaste);
    return () => {
      editor.root.removeEventListener('paste', handlePaste);
    };
  }, [canPost, submitting]);

  const handleInput = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleImageChange = (event) => {
    const selectedFile = event.target.files?.[0] || null;
    if (!selectedFile) {
      setImageFile(null);
      return;
    }

    if (!selectedFile.type.startsWith('image/')) {
      toast.error('Please select an image file.');
      setImageFile(null);
      return;
    }

    (async () => {
      try {
        setCompressingImage(true);
        setRemoveExistingImage(false);
        const compressed = await compressImageToLimit(selectedFile, MAX_UPLOAD_BYTES);
        setImageFile(compressed);

        toast.success(`Image optimized to ${Math.round(compressed.size / 1024)}KB.`);
      } catch (error) {
        console.error('Image compression failed:', error);
        toast.error('Failed to optimize image. Please try another image.');
        setImageFile(null);
      } finally {
        setCompressingImage(false);
      }
    })();
  };

  const toggleRole = (role) => {
    setFormData((prev) => {
      if (role === 'ALL') {
        return {
          ...prev,
          targetRoles: prev.targetRoles.includes('ALL') ? [] : ['ALL'],
        };
      }

      const withoutAll = prev.targetRoles.filter((r) => r !== 'ALL');
      const exists = withoutAll.includes(role);

      return {
        ...prev,
        targetRoles: exists ? withoutAll.filter((r) => r !== role) : [...withoutAll, role],
      };
    });
  };

  const handleEditNotice = (notice) => {
    if (!canManageNotice(notice)) {
      toast.error('You are not allowed to edit this notification.');
      return;
    }

    setEditingNoticeId(notice.id);
    setFormData(noticeToFormData(notice));
    setImageFile(null);
    setRemoveExistingImage(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDeleteNotice = async (notice) => {
    if (!canManageNotice(notice)) {
      toast.error('You are not allowed to delete this notification.');
      return;
    }

    if (!window.confirm('Delete this notification for everyone? This cannot be undone.')) {
      return;
    }

    try {
      await updatesService.deleteUpdate(notice.id);
      toast.success('Notification deleted successfully.');
      if (editingNoticeId === notice.id) {
        resetForm();
      }
      await loadUpdates();
    } catch (error) {
      console.error('Delete update error:', error);
      toast.error(error?.response?.data?.error || 'Failed to delete notification.');
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!canPost) {
      toast.error('You are not authorized to post notifications.');
      return;
    }

    const plainContent = stripHtml(formData.content);

    if (!formData.title.trim() || !plainContent) {
      toast.error('Title and content are required.');
      return;
    }

    if (!formData.targetRoles.length) {
      toast.error('Select at least one target role.');
      return;
    }

    if (isStudentTargetingEnabled) {
      if (!formData.studentSemesters.trim() || !formData.studentDepartments.trim()) {
        toast.error('Select semester and department for student targeting.');
        return;
      }
    }

    try {
      setSubmitting(true);
      const payload = new FormData();
      payload.append('title', formData.title.trim());
      const hasRichFormatting = /<(strong|em|u|s|a|ul|ol|li|blockquote|h[1-6]|code|pre)\b/i.test(formData.content);
      const normalizedContent = hasRichFormatting || !hasMarkdownSyntax(plainContent)
        ? formData.content.trim()
        : markdownToHtml(plainContent);
      payload.append('content', normalizedContent);
      payload.append('targetRoles', JSON.stringify(formData.targetRoles));
      payload.append('isActive', String(formData.isActive));
      if (editingNoticeId && removeExistingImage && !imageFile) {
        payload.append('removeImage', 'true');
      }
      if (isStudentTargetingEnabled) {
        if (selectedStudentUsns.length) {
          payload.append('studentUsns', selectedStudentUsns.join(', '));
        } else {
          payload.append('studentSemesters', formData.studentSemesters.trim());
          payload.append('studentDepartments', formData.studentDepartments.trim());
          if (formData.studentSections.trim()) {
            payload.append('studentSections', formData.studentSections.trim());
          }
        }
      }
      if (imageFile) payload.append('image', imageFile);

      if (editingNoticeId) {
        await updatesService.updateUpdate(editingNoticeId, payload);
        toast.success('Notification updated successfully.');
      } else {
        await updatesService.createUpdate(payload);
        toast.success('Notification posted successfully.');
      }

      resetForm();
      await loadUpdates();
    } catch (error) {
      console.error('Create update error:', error);
      toast.error(error?.response?.data?.error || (editingNoticeId ? 'Failed to update notification.' : 'Failed to post notification.'));
    } finally {
      setSubmitting(false);
    }
  };

  const matchingStudents = useMemo(() => {
    const semester = formData.studentSemesters.trim().toUpperCase();
    const department = formData.studentDepartments.trim().toUpperCase();
    const section = formData.studentSections.trim().toUpperCase();

    return students.filter((student) => {
      const studentSemester = String(student?.semester || '').trim().toUpperCase();
      const studentDepartment = String(student?.department || '').trim().toUpperCase();
      const studentSection = String(student?.section || '').trim().toUpperCase();

      return (!semester || studentSemester === semester)
        && (!department || studentDepartment === department)
        && (!section || studentSection === section);
    });
  }, [formData.studentDepartments, formData.studentSections, formData.studentSemesters, students]);

  const filteredStudents = useMemo(() => {
    const query = studentSearchQuery.trim().toLowerCase();
    if (!query) return matchingStudents;

    return matchingStudents.filter((student) => {
      const name = String(student?.name || '').toLowerCase();
      const usn = String(student?.usn || '').toLowerCase();
      const email = String(student?.email || '').toLowerCase();
      return name.includes(query) || usn.includes(query) || email.includes(query);
    });
  }, [matchingStudents, studentSearchQuery]);

  const selectedStudents = useMemo(
    () => matchingStudents.filter((student) => selectedStudentUsns.includes(student.usn)),
    [matchingStudents, selectedStudentUsns]
  );

  const openStudentPreview = async () => {
    if (!canPost || !isStudentTargetingEnabled) return;

    if (!formData.studentSemesters.trim() || !formData.studentDepartments.trim()) {
      toast.error('Select semester and department first.');
      return;
    }

    const loadedStudents = students.length ? students : await loadStudents();
    if (!loadedStudents.length) {
      toast.error('No students available to preview.');
      return;
    }

    setSelectedStudentUsns((current) => current.filter((usn) => loadedStudents.some((student) => student.usn === usn)));

    setStudentPreviewOpen(true);
  };

  const toggleStudentSelection = (studentUsn) => {
    setSelectedStudentUsns((current) => (
      current.includes(studentUsn)
        ? current.filter((value) => value !== studentUsn)
        : [...current, studentUsn]
    ));
  };

  return (
    <div className="min-h-[calc(100vh-200px)] bg-slate-50 py-6 sm:py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Notifications Management</h1>
          <p className="text-slate-600 mt-1">Post institution updates for students and staff.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          <section className="lg:col-span-2 bg-white rounded-xl border border-slate-200 shadow-sm p-4 sm:p-6">
            <div className="mb-4 flex items-center justify-between gap-3">
              <h2 className="text-lg font-semibold text-slate-900">{editingNoticeId ? 'Edit Notification' : 'Post Notification'}</h2>
              {editingNoticeId ? (
                <button
                  type="button"
                  onClick={resetForm}
                  className="text-xs px-3 py-1.5 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-100"
                >
                  Cancel edit
                </button>
              ) : null}
            </div>

            {!canPost && (
              <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-3 text-amber-800 text-sm">
                Your role is not authorized to publish notifications.
              </div>
            )}

            <form className="space-y-4" onSubmit={handleSubmit}>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Title</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInput}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                  placeholder="Enter notification title"
                  disabled={!canPost || submitting}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Content</label>
                <ReactQuill
                  ref={quillRef}
                  theme="snow"
                  value={formData.content}
                  onChange={(value) => setFormData((prev) => ({ ...prev, content: value }))}
                  modules={quillModules}
                  formats={quillFormats}
                  placeholder="Write your notification message"
                  readOnly={!canPost || submitting}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Image (optional)</label>
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/webp"
                  onChange={handleImageChange}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  disabled={!canPost || submitting || compressingImage}
                />
                {compressingImage ? (
                  <p className="text-xs text-slate-500 mt-1">Optimizing image to 100KB max...</p>
                ) : null}
                {!imageFile && formData.existingImageUrl && !removeExistingImage ? (
                  <div className="mt-2 rounded-lg border border-slate-200 bg-slate-50 p-2">
                    <div className="mb-2 flex items-center justify-between gap-2">
                      <p className="text-xs font-semibold text-slate-600">Current image</p>
                      <button
                        type="button"
                        onClick={() => setRemoveExistingImage(true)}
                        className="text-xs font-semibold text-rose-700 hover:text-rose-800"
                        disabled={!canPost || submitting}
                      >
                        Remove image
                      </button>
                    </div>
                    <img
                      src={formData.existingImageUrl}
                      alt="Current notice attachment"
                      className="max-h-48 w-full rounded-md object-cover border border-slate-200"
                    />
                  </div>
                ) : null}
                {removeExistingImage && !imageFile ? (
                  <div className="mt-2 rounded-lg border border-rose-200 bg-rose-50 p-2 text-xs text-rose-700 flex items-center justify-between gap-2">
                    <span>Current image will be removed when you save this notification.</span>
                    <button
                      type="button"
                      onClick={() => setRemoveExistingImage(false)}
                      className="font-semibold hover:text-rose-800"
                      disabled={!canPost || submitting}
                    >
                      Undo
                    </button>
                  </div>
                ) : null}
                {imageFile ? (
                  <p className="text-xs text-slate-500 mt-1">Selected: {imageFile.name} ({Math.round(imageFile.size / 1024)}KB)</p>
                ) : null}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Target Roles</label>
                <div className="grid grid-cols-2 gap-2 max-h-44 overflow-auto rounded-lg border border-slate-200 p-2">
                  {ROLE_OPTIONS.map((role) => (
                    <label key={role} className="flex items-center gap-2 text-xs text-slate-700">
                      <input
                        type="checkbox"
                        checked={formData.targetRoles.includes(role)}
                        onChange={() => toggleRole(role)}
                        disabled={!canPost || submitting}
                      />
                      {role}
                    </label>
                  ))}
                </div>
              </div>

              {isStudentTargetingEnabled ? (
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-3">
                  <div className="flex items-center justify-between gap-3">
                    <h3 className="text-sm font-semibold text-slate-900">Student Target Filters</h3>
                    <button
                      type="button"
                      onClick={openStudentPreview}
                      className="text-xs font-semibold rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-slate-700 hover:bg-slate-100 disabled:opacity-60 disabled:cursor-not-allowed"
                      disabled={!canPost || submitting || studentsLoading}
                    >
                      Preview Students
                    </button>
                  </div>
                  {studentsError ? (
                    <p className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">{studentsError}</p>
                  ) : null}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-1">Semester</label>
                      <select
                        name="studentSemesters"
                        value={formData.studentSemesters}
                        onChange={handleInput}
                        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                        disabled={!canPost || submitting}
                      >
                        <option value="">Select semester</option>
                        {SEMESTER_OPTIONS.map((semester) => (
                          <option key={semester} value={semester}>{semester.replace('SEM_', 'SEM ')}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-1">Department</label>
                      <select
                        name="studentDepartments"
                        value={formData.studentDepartments}
                        onChange={handleInput}
                        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                        disabled={!canPost || submitting}
                      >
                        <option value="">Select department</option>
                        {DEPARTMENT_OPTIONS.map((department) => (
                          <option key={department} value={department}>{department}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-1">Section</label>
                      <select
                        name="studentSections"
                        value={formData.studentSections}
                        onChange={handleInput}
                        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                        disabled={!canPost || submitting}
                      >
                        <option value="">All Sections</option>
                        {SECTION_OPTIONS.map((section) => (
                          <option key={section} value={section}>{section}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <p className="text-xs text-slate-500">Choose semester and department. Leave section on All Sections to target the full class.</p>
                </div>
              ) : null}

              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleInput}
                  disabled={!canPost || submitting}
                />
                Mark as active
              </label>

              <button
                type="submit"
                disabled={!canPost || submitting || compressingImage}
                className="w-full rounded-lg bg-primary text-white py-2.5 text-sm font-semibold hover:bg-primary-dark disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {compressingImage ? 'Optimizing image...' : submitting ? (editingNoticeId ? 'Saving...' : 'Posting...') : (editingNoticeId ? 'Save Changes' : 'Post Notification')}
              </button>
            </form>
          </section>

          <section className="lg:col-span-3 bg-white rounded-xl border border-slate-200 shadow-sm p-4 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-slate-900">Current Active Notifications</h2>
              <button
                onClick={loadUpdates}
                className="text-sm px-3 py-1.5 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-100"
              >
                Refresh
              </button>
            </div>

            {loading ? (
              <div className="text-slate-500 text-sm">Loading notifications...</div>
            ) : updates.length === 0 ? (
              <div className="text-slate-500 text-sm">No active notifications available.</div>
            ) : (
              <div className="space-y-3 max-h-[680px] overflow-auto pr-1">
                {updates.map((item) => (
                  <PostCard
                    key={item.id}
                    title={item.title}
                    content={item.content}
                    imageUrl={item.imageUrl}
                    authorName={item.postedByName || 'Administration'}
                    authorMeta={item.postedByRole ? `@${item.postedByRole.toLowerCase()}` : '@administration'}
                    timestamp={formatDate(item.publishedAt || item.createdAt)}
                    notificationId={item.id}
                    initialLikes={item.likes || 0}
                    initialDislikes={item.dislikes || 0}
                    userReaction={item.userReaction || null}
                    primaryActionLabel="Acknowledge"
                    primaryActionDoneLabel="Archived"
                    secondaryActionLabel="Mark Important"
                    secondaryActionDoneLabel="Marked Important"
                    onPrimaryAction={async () => {
                      await updatesService.archiveUpdate(item.id);
                      setUpdates((prev) => prev.filter((entry) => entry.id !== item.id));
                      toast.success('Notification moved to archived list.');
                    }}
                    managementActions={canManageNotice(item) ? (
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => handleEditNotice(item)}
                          className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteNotice(item)}
                          className="rounded-lg border border-rose-300 px-3 py-1.5 text-xs font-semibold text-rose-700 hover:bg-rose-50"
                        >
                          Delete
                        </button>
                      </div>
                    ) : null}
                    footer={
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                        <span><strong>Published:</strong> {formatDate(item.publishedAt || item.createdAt)}</span>
                        <span className="sm:col-span-2">
                          <strong>Targets:</strong> {(item.targetRoles || []).join(', ') || '—'}
                        </span>
                        {item.audience ? (
                          <span className="sm:col-span-2">
                            <strong>Student Filters:</strong>{' '}
                            {[
                              item.audience.semesters?.length ? `Sem: ${item.audience.semesters.join(', ')}` : null,
                              item.audience.departments?.length ? `Dept: ${item.audience.departments.join(', ')}` : null,
                              item.audience.sections?.length ? `Section: ${item.audience.sections.join(', ')}` : null,
                            ]
                              .filter(Boolean)
                              .join(' | ') || '—'}
                          </span>
                        ) : null}
                      </div>
                    }
                  />
                ))}
              </div>
            )}
          </section>
        </div>
      </div>

      {studentPreviewOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 px-4 py-8">
          <div className="flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-slate-200">
            <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-5 py-4">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">Matching Students</h3>
                <p className="text-sm text-slate-600">
                  {formData.studentSemesters || 'Any'} · {formData.studentDepartments || 'Any'} · {formData.studentSections || 'Any'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setStudentPreviewOpen(false)}
                className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-100"
              >
                Close
              </button>
            </div>

            <div className="flex-1 overflow-auto px-5 py-4">
              {studentsLoading ? (
                <div className="py-10 text-center text-sm text-slate-500">Loading students...</div>
              ) : matchingStudents.length === 0 ? (
                <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">
                  No students match the selected filters.
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-slate-50 px-4 py-3 text-sm text-slate-700">
                    <span>
                      Showing {matchingStudents.length} student{matchingStudents.length === 1 ? '' : 's'}
                      {selectedStudents.length ? `, ${selectedStudents.length} selected` : ''}
                    </span>
                    {selectedStudentUsns.length ? (
                      <button
                        type="button"
                        onClick={() => setSelectedStudentUsns([])}
                        className="text-xs font-semibold text-rose-700 hover:text-rose-800"
                      >
                        Clear selection
                      </button>
                    ) : null}
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
                    <label className="block text-xs font-medium text-slate-700 mb-1">Search students</label>
                    <input
                      type="text"
                      value={studentSearchQuery}
                      onChange={(event) => setStudentSearchQuery(event.target.value)}
                      placeholder="Search by name, USN, or email"
                      className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                    />
                  </div>
                  <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                    {filteredStudents.map((student) => (
                      <div
                        key={student.id || student.usn}
                        className={`h-full rounded-xl border p-4 shadow-sm transition ${selectedStudentUsns.includes(student.usn) ? 'border-primary bg-primary/5 ring-1 ring-primary/20' : 'border-slate-200 bg-white'}`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <label className="flex min-w-0 flex-1 cursor-pointer items-start gap-3">
                            <input
                              type="checkbox"
                              checked={selectedStudentUsns.includes(student.usn)}
                              onChange={() => toggleStudentSelection(student.usn)}
                              className="mt-1 h-4 w-4 shrink-0"
                            />
                            <div className="min-w-0 flex-1">
                              <p className="break-words font-semibold text-slate-900">{student.name || 'Unnamed student'}</p>
                              <p className="break-all text-sm text-slate-500">{student.usn || 'No USN'}</p>
                            </div>
                          </label>
                          <span className="shrink-0 rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-emerald-700">
                            {student.status || 'ACTIVE'}
                          </span>
                        </div>
                        <div className="mt-3 grid grid-cols-1 gap-2 text-xs text-slate-600 sm:grid-cols-2">
                          <span className="min-w-0 break-words"><strong>Semester:</strong> {student.semester || '—'}</span>
                          <span className="min-w-0 break-words"><strong>Department:</strong> {student.department || '—'}</span>
                          <span className="min-w-0 break-words"><strong>Section:</strong> {student.section || '—'}</span>
                          <span className="min-w-0 break-all"><strong>Email:</strong> {student.email || '—'}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between gap-3 border-t border-slate-200 px-5 py-4">
              <p className="text-sm text-slate-600">
                {selectedStudentUsns.length
                  ? `${selectedStudentUsns.length} selected students will be targeted directly.`
                  : 'No students selected yet. The notice will use the semester/department/section filters.'}
              </p>
              <button
                type="button"
                onClick={() => setStudentPreviewOpen(false)}
                className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary-dark"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default NotificationsManagement;
